import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { SearchableSelect } from '../shared/SearchableSelect';
import { CreateLocationPopover } from '../shared/CreateLocationPopover';
import type {
  TransmitterFormData,
  TransmitterOptions,
  TransmitterV2Document,
  HardwareOption,
} from '../../types';

interface ConfigDataSectionProps {
  formData: TransmitterFormData;
  setField: <K extends keyof TransmitterFormData>(key: K, value: TransmitterFormData[K]) => void;
  options: TransmitterOptions;
  transmitter: TransmitterV2Document;
  isAdmin: boolean;
  hardwareBase: HardwareOption[];
  hardwareModule: HardwareOption[];
  hardwareRadio: HardwareOption[];
  onRefreshOptions?: () => void;
  canCreateLocation?: boolean;
}

export function ConfigDataSection({
  formData,
  setField,
  options,
  transmitter,
  isAdmin,
  hardwareBase,
  hardwareModule,
  hardwareRadio,
  onRefreshOptions,
  canCreateLocation = false,
}: ConfigDataSectionProps) {
  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setField('gps', [pos.coords.latitude, pos.coords.longitude]);
        toast.success('Location updated');
      },
      () => toast.error('Unable to get your location'),
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        Configuration Data
      </h3>

      {/* Name + Activated toggle */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Name</Label>
        <div className="flex items-center gap-2">
          <Input
            value={formData.name}
            onChange={(e) => setField('name', e.target.value)}
            className="flex-1"
          />
          {isAdmin && (
            <Button
              type="button"
              variant={formData.activated ? 'default' : 'destructive'}
              size="sm"
              onClick={() => setField('activated', !formData.activated)}
              className="shrink-0"
            >
              {formData.activated ? 'Activated' : 'Inactive'}
            </Button>
          )}
        </div>
      </div>

      {/* ID (read-only) */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>ID</Label>
        <Input value={transmitter._id} disabled />
      </div>

      {/* Product ID (read-only) */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Product ID</Label>
        <Input value={transmitter.dev_id} disabled />
      </div>

      {/* XBee Serial (read-only display) */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>XBee Serial</Label>
        <Input value={transmitter.xbee_SL || ''} disabled />
      </div>

      {/* User Group */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>User Group</Label>
        <SearchableSelect
          value={formData.usergroup}
          onValueChange={(val) => setField('usergroup', val)}
          options={options.usergroups}
          placeholder="Select user group"
          disabled={!isAdmin}
        />
      </div>

      {/* Site */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Site</Label>
        <SearchableSelect
          value={formData.site}
          onValueChange={(val) => setField('site', val)}
          options={options.sites}
          placeholder="Search site"
        />
      </div>

      {/* Location */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Location</Label>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <SearchableSelect
              value={formData.location}
              onValueChange={(val) => setField('location', val)}
              options={options.locations}
              placeholder="Search location"
            />
          </div>
          {canCreateLocation && (
            <CreateLocationPopover
              currentSite={formData.site}
              currentOrg={formData.organisation}
              onCreated={(loc) => {
                setField('location', loc._id);
                onRefreshOptions?.();
              }}
            />
          )}
        </div>
      </div>

      {/* Streams (admin only) */}
      {isAdmin && (
        <div className="grid grid-cols-[140px_1fr] items-start gap-2">
          <Label className="pt-2">Streams</Label>
          <div className="space-y-1">
            {transmitter.streams.length === 0 ? (
              <span className="text-sm text-muted-foreground">No streams</span>
            ) : (
              transmitter.streams.map((s) => (
                <div key={s._id} className="text-sm border rounded p-2">
                  <span className="font-mono text-xs">{s.name}</span>
                  {s.sensor ? (
                    <span className="text-muted-foreground ml-2">— {s.sensor.name}</span>
                  ) : (
                    <span className="text-muted-foreground ml-2">— No sensor</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* GPS */}
      {isAdmin && (
        <div className="grid grid-cols-[140px_1fr] items-center gap-2">
          <Label>Latitude, Longitude</Label>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">(</span>
            <Input
              type="number"
              step="any"
              className="w-32"
              value={formData.gps[0] ?? ''}
              onChange={(e) =>
                setField('gps', [
                  e.target.value ? Number(e.target.value) : null,
                  formData.gps[1],
                ])
              }
              placeholder="Lat"
            />
            <span className="text-muted-foreground">,</span>
            <Input
              type="number"
              step="any"
              className="w-32"
              value={formData.gps[1] ?? ''}
              onChange={(e) =>
                setField('gps', [
                  formData.gps[0],
                  e.target.value ? Number(e.target.value) : null,
                ])
              }
              placeholder="Lng"
            />
            <span className="text-muted-foreground">)</span>
            <Button type="button" variant="outline" size="sm" onClick={handleGeolocation}>
              Use Current Geolocation
            </Button>
          </div>
        </div>
      )}

      {/* Current Firmware (read-only) */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Current Firmware</Label>
        <span className="text-sm">
          {transmitter.current_firmware_name || 'Unknown'}
        </span>
      </div>

      {/* Hardware dropdowns */}
      {isAdmin && (
        <>
          <div className="grid grid-cols-[140px_1fr] items-center gap-2">
            <Label>Hardware</Label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <span className="text-xs text-muted-foreground">Base</span>
                <Select
                  value={formData.hardware_base ?? ''}
                  onValueChange={(val) => setField('hardware_base', val || null)}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="--" />
                  </SelectTrigger>
                  <SelectContent>
                    {hardwareBase.map((hw) => (
                      <SelectItem key={hw._id} value={hw._id}>
                        {hw.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">Module</span>
                <Select
                  value={formData.hardware_module ?? ''}
                  onValueChange={(val) => setField('hardware_module', val || null)}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="--" />
                  </SelectTrigger>
                  <SelectContent>
                    {hardwareModule.map((hw) => (
                      <SelectItem key={hw._id} value={hw._id}>
                        {hw.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">Connectivity</span>
                <Select
                  value={formData.hardware_radio ?? ''}
                  onValueChange={(val) => setField('hardware_radio', val || null)}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="--" />
                  </SelectTrigger>
                  <SelectContent>
                    {hardwareRadio.map((hw) => (
                      <SelectItem key={hw._id} value={hw._id}>
                        {hw.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Remote Configs & Local Alerts */}
      <div className="grid grid-cols-[140px_1fr] items-start gap-2">
        <Label className="pt-1">Remote Configs</Label>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={formData.remote_config_enabled}
              onCheckedChange={(checked) =>
                setField('remote_config_enabled', !!checked)
              }
            />
            Activated
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={formData.local_alerts_enabled}
              onCheckedChange={(checked) =>
                setField('local_alerts_enabled', !!checked)
              }
            />
            Enable Local Alerts
          </label>
        </div>
      </div>

      {/* Data Logging */}
      <div className="grid grid-cols-[140px_1fr] items-start gap-2">
        <Label className="pt-1">Data Logging</Label>
        <div className="flex gap-6">
          {(['rssi_stream', 'dc1_stream', 'dc2_stream', 'bv_stream'] as const).map(
            (key) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={formData[key]}
                  onCheckedChange={(checked) => setField(key, !!checked)}
                />
                {key === 'rssi_stream'
                  ? 'RSSI'
                  : key === 'dc1_stream'
                    ? 'DC1'
                    : key === 'dc2_stream'
                      ? 'DC2'
                      : 'Battery'}
              </label>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
